import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Eye, Target, ListChecks, PenTool, Gift, ChevronLeft, ChevronRight, BookOpen, ChevronDown } from "lucide-react";
import { Reassurance, CardLogos } from "@/components/PaymentTrust";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/kit";
import { MISSIONS, CONTENT } from "@/modules/premiere-video/content";
import { MISSION_PREVIEW } from "@/modules/premiere-video/preview";
import { useProgress, OWNER_KEY } from "@/modules/premiere-video/store";
import { missionStatus } from "@/modules/premiere-video/logic";

// Owner key is only set once a signed-in client with access has synced her progress.
// Also requires the current Clerk session to belong to that owner (window.Clerk, no hook: home also renders without Clerk).
const useOwnerStatuses = () => {
  const { state } = useProgress();
  const [clerkUser, setClerkUser] = useState(() => window.Clerk?.user?.id || null);
  useEffect(() => {
    let unsub = null;
    let tries = 0;
    const attach = () => {
      const c = window.Clerk;
      if (c?.addListener) {
        unsub = c.addListener(({ user }) => setClerkUser(user?.id || null));
        return true;
      }
      return false;
    };
    if (attach()) return () => unsub?.();
    const t = setInterval(() => { if (attach() || ++tries > 40) clearInterval(t); }, 250);
    return () => { clearInterval(t); unsub?.(); };
  }, []);
  const owner = localStorage.getItem(OWNER_KEY);
  if (!owner || owner !== clerkUser) return null;
  return Object.fromEntries(MISSIONS.map((m) => [m.id, missionStatus(state, m.id)]));
};

function PreviewBlock({ Icon, title, children }) {
  return (
    <div className="flex gap-4">
      <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-ivory text-bx"><Icon className="h-4 w-4" strokeWidth={1.6} /></span>
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-bx-2">{title}</p>
        <div className="mt-1.5 text-base leading-relaxed text-ink/85">{children}</div>
      </div>
    </div>
  );
}

function FreeSample() {
  const [open, setOpen] = useState(false);
  const sec = CONTENT[1].sections[0];
  const paras = Array.isArray(sec.text) ? sec.text : sec.text ? [sec.text] : [];
  return (
    <div className="rounded-2xl border border-gold/40 bg-ivory/70" data-testid="free-sample">
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} data-testid="free-sample-toggle"
        className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left">
        <span className="flex items-center gap-3">
          <BookOpen className="h-4 w-4 shrink-0 text-bx" strokeWidth={1.6} />
          <span>
            <span className="block text-xs font-semibold uppercase tracking-[0.18em] text-bx-2">Extrait gratuit</span>
            <span className="block font-medium text-ink">Lire la première partie de la mission</span>
          </span>
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-bx transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="animate-fade-up border-t border-gold/30 px-5 pb-5 pt-4" data-testid="free-sample-content">
          <h4 className="font-display text-xl font-semibold text-bx">{sec.title}</h4>
          <div className="mt-3 space-y-3 text-base leading-relaxed text-ink/85">
            {paras.map((t) => <p key={t}>{t}</p>)}
            {sec.list && (
              <ul className="space-y-2">
                {sec.list.map((it) => <li key={it.t}><span className="font-semibold text-ink">{it.t} : </span>{it.d}</li>)}
              </ul>
            )}
          </div>
          <p className="mt-4 text-sm text-soft">La suite de la mission, le quiz et les 6 autres missions sont inclus dans le parcours complet.</p>
          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
            <Link to="/acces" data-testid="free-sample-unlock"
              className="group inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full bg-bx px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-bx-2 sm:w-auto">
              Débloquer la suite – 9 €<ArrowRight className="h-4 w-4 shrink-0 transition-transform group-hover:translate-x-1" />
            </Link>
            <Reassurance testId="free-sample-reassurance" className="sm:justify-start" />
          </div>
          <CardLogos testId="free-sample-card-logos" className="mt-3 sm:justify-start" />
        </div>
      )}
    </div>
  );
}

function MissionPreviewDialog({ id, onChange, statuses }) {
  const m = MISSIONS.find((x) => x.id === id);
  if (!m) return null;
  const c = CONTENT[id];
  const p = MISSION_PREVIEW[id];
  const cta = statuses?.[id] === "done" ? "Revoir la mission" : statuses?.[id] === "progress" ? "Continuer la mission" : "Commencer cette mission";
  return (
    <Dialog open onOpenChange={(o) => !o && onChange(null)}>
      <DialogContent className="max-h-[90vh] w-[calc(100%-2rem)] max-w-xl grid-cols-[minmax(0,1fr)] gap-0 overflow-y-auto rounded-[20px] border-line bg-white p-0 sm:rounded-[20px] [&>button:last-child]:text-white [&>button:last-child]:opacity-80" data-testid="mission-preview-dialog">
        <div className="rounded-t-[20px] bg-bx px-6 pb-6 pt-7 text-white sm:px-8">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold lining-nums">Mission {m.num} / 07</p>
          <DialogTitle className="mt-2 font-display text-2xl font-semibold leading-snug sm:text-3xl" data-testid="mission-preview-title">{m.title}</DialogTitle>
          <DialogDescription className="mt-2 text-base text-white/80">{m.short}</DialogDescription>
          {statuses && <div className="mt-4"><StatusBadge status={statuses[id]} testId="mission-preview-status" /></div>}
        </div>
        <div className="space-y-6 px-6 py-7 sm:px-8">
          <PreviewBlock Icon={Target} title="Objectif">{c.objective}</PreviewBlock>
          <PreviewBlock Icon={ListChecks} title="Au programme">
            <ul className="space-y-1" data-testid="mission-preview-program">
              {c.sections.map((s) => <li key={s.title} className="flex gap-2"><span className="mt-2.5 h-1 w-1 shrink-0 rounded-full bg-gold" />{s.title}</li>)}
              {c.example && <li className="flex gap-2"><span className="mt-2.5 h-1 w-1 shrink-0 rounded-full bg-gold" />Exemple concret : {c.example.title}</li>}
            </ul>
          </PreviewBlock>
          <PreviewBlock Icon={PenTool} title="À toi de jouer">{p.practice}</PreviewBlock>
          <PreviewBlock Icon={Gift} title="Tu repars avec">{p.outcome}</PreviewBlock>
          {id === 1 && !statuses && <FreeSample />}
        </div>
        <div className="flex flex-col-reverse gap-4 border-t border-line px-6 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-8">
          <div className="flex justify-center gap-2 sm:justify-start">
            <button type="button" disabled={id === 1} onClick={() => onChange(id - 1)} data-testid="mission-preview-prev"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-line text-bx transition-colors hover:border-bx/40 disabled:opacity-30" aria-label="Mission précédente"><ChevronLeft className="h-4 w-4" /></button>
            <button type="button" disabled={id === 7} onClick={() => onChange(id + 1)} data-testid="mission-preview-next"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-line text-bx transition-colors hover:border-bx/40 disabled:opacity-30" aria-label="Mission suivante"><ChevronRight className="h-4 w-4" /></button>
          </div>
          <Link to={`/mission/${id}`} data-testid="mission-preview-open"
            className="group inline-flex w-full min-w-0 items-center justify-center gap-2 whitespace-nowrap rounded-full sm:w-auto bg-bx px-5 sm:px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-bx-2">
            {cta}<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MissionCard({ m, wide, status, onPreview }) {
  return (
    <article data-testid={`home-mission-card-${m.id}`}
      className={`group relative flex flex-col overflow-hidden rounded-[20px] border border-line bg-white p-6 shadow-[0_2px_10px_-4px_rgba(36,33,39,0.08)] transition-[transform,box-shadow,border-color] duration-300 ease-out hover:-translate-y-1 hover:border-bx/30 hover:shadow-[0_18px_40px_-18px_rgba(101,31,56,0.35)] sm:p-7 ${wide ? "md:col-span-2" : ""}`}>
      <span className="absolute left-6 top-0 h-[3px] w-12 rounded-b-full bg-gold transition-[width,background-color] duration-500 ease-out group-hover:w-[calc(100%-3rem)] group-hover:bg-bx sm:left-7 sm:group-hover:w-[calc(100%-3.5rem)]" aria-hidden />
      <div className="flex items-start justify-between gap-4">
        <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full font-display text-lg lining-nums tabular-nums ring-1 transition-colors duration-300 group-hover:bg-bx group-hover:text-white group-hover:ring-bx ${status === "done" ? "bg-bx text-white ring-bx" : "text-bx ring-gold/70"}`} data-testid={`home-mission-number-${m.id}`}>
          {m.num}
        </span>
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-ivory text-bx-2 transition-colors duration-300 group-hover:text-bx">
          <m.icon className="h-5 w-5" strokeWidth={1.5} aria-hidden />
        </span>
      </div>
      <h3 className="mt-6 font-display text-xl font-semibold text-ink transition-colors duration-300 group-hover:text-bx sm:text-2xl">{m.title}</h3>
      {status && <div className="mt-3"><StatusBadge status={status} testId={`home-mission-status-${m.id}`} /></div>}
      <p className="mt-3 flex-1 text-base leading-relaxed text-soft">{m.short}</p>
      <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3">
        <Link to={`/mission/${m.id}`} data-testid={`home-mission-link-${m.id}`} className="inline-flex items-center gap-2 text-sm font-semibold text-bx">
          <span data-testid={`home-mission-discover-${m.id}`}>{status === "progress" ? "Continuer la mission" : status === "done" ? "Revoir la mission" : "Découvrir la mission"}</span>
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden />
        </Link>
        <button type="button" onClick={onPreview} data-testid={`home-mission-preview-${m.id}`}
          className="inline-flex items-center gap-2 text-sm font-medium text-soft transition-colors hover:text-bx">
          <Eye className="h-4 w-4" strokeWidth={1.6} />Aperçu
        </button>
      </div>
    </article>
  );
}

export function MissionCards() {
  const [open, setOpen] = useState(null);
  const statuses = useOwnerStatuses();
  return (
    <>
      <div className="mt-10 grid gap-5 md:grid-cols-2 md:gap-6">
        {MISSIONS.map((m, i) => (
          <MissionCard key={m.id} m={m} wide={i === MISSIONS.length - 1} status={statuses?.[m.id]} onPreview={() => setOpen(m.id)} />
        ))}
      </div>
      {open && <MissionPreviewDialog id={open} onChange={setOpen} statuses={statuses} />}
    </>
  );
}
